import API_CONFIG from '../config/api';

export interface TestPackage {
  _id: string;
  name: string;
  description: string;
  price: number;
  discountPrice: number;
  category: 'common' | 'specialized' | 'discount' | 'emergency';
  priority: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestPackageData {
  name: string;
  description?: string;
  price: number;
  discountPrice: number;
  category: 'common' | 'specialized' | 'discount' | 'emergency';
  priority?: number;
  active?: boolean;
}

export interface UpdateTestPackageData {
  name?: string;
  description?: string;
  price?: number;
  discountPrice?: number;
  category?: 'common' | 'specialized' | 'discount' | 'emergency';
  priority?: number;
  active?: boolean;
}

// Function to get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

/**
 * Get active test packages (for public use)
 */
export const getActiveTestPackages = async (): Promise<TestPackage[]> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('test-packages/active'), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت پکیج های فعال');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get all test packages (admin only)
 */
export const getAllTestPackages = async (): Promise<TestPackage[]> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('test-packages'), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت پکیج ها');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get active test package by ID (for public use)
 */
export const getActiveTestPackageById = async (id: string): Promise<TestPackage> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`test-packages/details/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت پکیج');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Get test package by ID (admin only)
 */
export const getTestPackageById = async (id: string): Promise<TestPackage> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`test-packages/${id}`), {
      method: 'GET',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در دریافت پکیج');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Create a new test package (admin only)
 */
export const createTestPackage = async (data: CreateTestPackageData): Promise<TestPackage> => {
  try {
    const response = await fetch(API_CONFIG.getUrl('test-packages'), {
      method: 'POST',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در ایجاد پکیج');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Update a test package (admin only)
 */
export const updateTestPackage = async (id: string, data: UpdateTestPackageData): Promise<TestPackage> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`test-packages/${id}`), {
      method: 'PUT',
      ...getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در به‌روزرسانی پکیج');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Toggle test package status (admin only)
 */
export const toggleTestPackageStatus = async (id: string): Promise<TestPackage> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`test-packages/${id}/toggle-status`), {
      method: 'PUT',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.message || 'خطا در تغییر وضعیت پکیج');
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Delete a test package (admin only)
 */
export const deleteTestPackage = async (id: string): Promise<void> => {
  try {
    const response = await fetch(API_CONFIG.getUrl(`test-packages/${id}`), {
      method: 'DELETE',
      ...getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || 'خطا در حذف پکیج');
    }
  } catch (error) {

    throw error;
  }
}; 